/**
 * useSaveSquareTrainerResult
 *
 * POST /fun_challenges/v1/square_trainer_results/
 *
 * Idempotent on `session_id` server-side (Django uses update_or_create).
 * Both clients fire this on `fun_session_end`; the second call is a no-op.
 *
 * Auth: bearer token is auto-injected by `ChessRestClient`'s request
 * interceptor. The server additionally rejects writes where
 * `request.user.user_id` is neither `player1_id` nor `player2_id`.
 */

import {useMutation} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';

export interface SaveSquareTrainerResultPayload {
  session_id: string;
  game_type: string;
  player1_id: number;
  player2_id: number;
  result: 'p1' | 'p2' | 'draw';
  reason: 'time_up' | 'forfeit';
  squares: string[];
  p1_responses: string[];
  p2_responses: string[];
  p1_score: number;
  p1_wrong: number;
  p1_accuracy: number;
  p2_score: number;
  p2_wrong: number;
  p2_accuracy: number;
  time_control_sec: number;
  orientation: 'white' | 'black' | 'random';
  p1_started_at?: string | null;
  p2_started_at?: string | null;
  p1_finished_at?: string | null;
  p2_finished_at?: string | null;
}

export interface SaveSquareTrainerResultResponse {
  ok: boolean;
  history_id?: number;
  error?: string;
}

export default function useSaveSquareTrainerResult(
  onSuccess?: (data: SaveSquareTrainerResultResponse) => void,
  onError?: (err: unknown) => void,
) {
  return useMutation<
    SaveSquareTrainerResultResponse,
    unknown,
    SaveSquareTrainerResultPayload
  >(
    async (payload: SaveSquareTrainerResultPayload) => {
      const response = await ChessRestClient.post(
        'fun_challenges/v1/square_trainer_results/',
        payload,
      );
      return response.data as SaveSquareTrainerResultResponse;
    },
    {
      onSuccess: data => {
        if (onSuccess) onSuccess(data);
      },
      onError: err => {
        if (onError) onError(err);
      },
    },
  );
}
